import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
import { UserPlus, ChevronDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import { usePmProperty } from '@/hooks/usePmProperty';
import {
  getPmUsers,
  getPendingRegistrations,
  suspendUser,
  reinstateUser,
  approveRegistration,
  rejectRegistration,
  getPropertyConfig,
  getUnits,
  assignUserUnit,
} from '@/services/api.client';
import { isCommercial } from '@/lib/property-type';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/pm/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { InviteUserDialog, type InviteRole } from '@/components/pm/InviteUserDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  PmUser,
  PendingRegistration,
  UserRole,
  PmUserStatus,
  PaginatedResponse,
  PropertyType,
} from '@/types';
import { PaginationControls } from '@/components/ui/pagination-controls';

const ROLE_LABELS: Partial<Record<UserRole, string>> = {
  resident: 'Resident',
  guard: 'Guard',
  security_supervisor: 'Supervisor',
  property_manager: 'Manager',
  enterprise_tenant_admin: 'Enterprise',
};

const STATUS_VARIANTS: Record<PmUserStatus, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  suspended: 'outline',
  pending: 'secondary',
};

type TabKey = 'all' | 'residents' | 'guards' | 'supervisors' | 'pending';
type RoleFilter = UserRole | undefined;

const TAB_ROLES: Record<TabKey, RoleFilter> = {
  all: undefined,
  residents: 'resident',
  guards: 'guard',
  supervisors: 'security_supervisor',
  pending: undefined,
};

const TAB_LABELS: Record<TabKey, string> = {
  all: 'All',
  residents: 'Residents',
  guards: 'Guards',
  supervisors: 'Supervisors',
  pending: 'Pending',
};

// Tenant company management lives on its own dedicated /manager/companies
// page (ADR 009 slice 3) — much richer than a tab and the BM mental model
// for "managing my building's companies" is a separate task from "managing
// my building's people". The Residents tab still hides on commercial
// properties because there are no residents on a pure office building.
function tabsForPropertyType(t: PropertyType | undefined): TabKey[] {
  if (isCommercial(t)) return ['all', 'guards', 'supervisors', 'pending'];
  return ['all', 'residents', 'guards', 'supervisors', 'pending'];
}

interface SuspendState {
  userId: string;
  action: 'suspend' | 'reinstate';
}

interface AssignUnitState {
  userId: string;
  userName: string;
}

export function UsersPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('all');
  const [page, setPage] = useState(1);
  const [suspendState, setSuspendState] = useState<SuspendState | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [inviteRole, setInviteRole] = useState<InviteRole | null>(null);
  const [assignUnitState, setAssignUnitState] = useState<AssignUnitState | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const propertyConfigQuery = useQuery({
    queryKey: ['pm', 'property-config', propertyId],
    queryFn: () => getPropertyConfig(propertyId),
    enabled: !!propertyId,
  });
  const propertyType = propertyConfigQuery.data?.propertyType;
  const availableTabs = tabsForPropertyType(propertyType);

  // If the active tab is no longer relevant for this property type
  // (e.g. propertyType resolved to 'commercial' while tab='residents'),
  // fall back to 'all'.
  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab('all');
      setPage(1);
    }
  }, [availableTabs, tab]);

  const usersQuery = useQuery({
    queryKey: ['pm', 'users', propertyId, TAB_ROLES[tab], page, debouncedSearch],
    queryFn: () => {
      const role = TAB_ROLES[tab];
      return getPmUsers(
        propertyId,
        role !== undefined
          ? { role, page, perPage: 20, ...(debouncedSearch && { search: debouncedSearch }) }
          : { page, perPage: 20, ...(debouncedSearch && { search: debouncedSearch }) },
      );
    },
    enabled: !!propertyId && tab !== 'pending',
    placeholderData: keepPreviousData,
  });

  const pendingQuery = useQuery({
    queryKey: ['pm', 'pending-registrations', propertyId],
    queryFn: () => getPendingRegistrations(propertyId),
    enabled: !!propertyId,
  });

  const suspendMutation = useMutation({
    mutationFn: () =>
      suspendState?.action === 'suspend'
        ? suspendUser(propertyId, suspendState.userId)
        : reinstateUser(propertyId, suspendState?.userId ?? ''),
    onMutate: async () => {
      const queryKey = ['pm', 'users', propertyId, TAB_ROLES[tab], page, debouncedSearch];
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<PaginatedResponse<PmUser>>(queryKey);
      if (suspendState) {
        const newStatus: PmUserStatus = suspendState.action === 'suspend' ? 'suspended' : 'active';
        qc.setQueryData<PaginatedResponse<PmUser>>(queryKey, (old) =>
          old
            ? {
                ...old,
                data: old.data.map((u) =>
                  u.id === suspendState.userId ? { ...u, status: newStatus } : u,
                ),
              }
            : old,
        );
      }
      return { previous, queryKey };
    },
    onSuccess: () => {
      setSuspendState(null);
    },
    onError: (_err, _args, context) => {
      if (context?.previous && context.queryKey) {
        qc.setQueryData(context.queryKey, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'users', propertyId] });
      setSuspendState(null);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (userId: string) => approveRegistration(propertyId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'pending-registrations', propertyId] });
      // Approval may auto-provision a unit from the resident's typed reference,
      // so refresh the units list too.
      void qc.invalidateQueries({ queryKey: ['pm', 'units', propertyId] });
      toast.success('Registration approved');
    },
    onError: (err) => {
      const message = (err as { message?: string }).message ?? 'Approval failed. Please try again.';
      toast.error(message);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      rejectRegistration(propertyId, userId, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'pending-registrations', propertyId] });
      setRejectTarget(null);
      toast.success('Registration rejected');
    },
  });

  const unitsQuery = useQuery({
    queryKey: ['pm', 'units', propertyId],
    queryFn: () => getUnits(propertyId, { limit: 500 }),
    enabled: !!propertyId && !!assignUnitState,
  });

  const assignUnitMutation = useMutation({
    mutationFn: ({ userId, unitId }: { userId: string; unitId: string }) =>
      assignUserUnit(propertyId, userId, unitId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'users', propertyId] });
      void qc.invalidateQueries({ queryKey: ['pm', 'pending-registrations', propertyId] });
      setAssignUnitState(null);
      setSelectedUnitId('');
      toast.success('Unit assigned');
    },
    onError: () => {
      toast.error('Failed to assign unit. Please try again.');
    },
  });

  const users = usersQuery.data?.data ?? [];
  const pending = pendingQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Users</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm">
              <UserPlus className="h-4 w-4 mr-2" /> Invite User
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {propertyType !== 'commercial_single' && propertyType !== 'commercial_multi' && (
              <DropdownMenuItem onSelect={() => setInviteRole('resident')}>
                Invite Resident
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setInviteRole('guard')}>
              Invite Guard
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setInviteRole('security_supervisor')}>
              Invite Supervisor
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setInviteRole('property_manager')}>
              Invite Manager
            </DropdownMenuItem>
            {/* Enterprise admin invite uses the enterprise-tenant setup flow, not
                this PM users surface — omitted from this dropdown. */}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v as TabKey);
          setPage(1);
        }}
      >
        {tab !== 'pending' && (
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
        )}
        <TabsList>
          {availableTabs.map((t) =>
            t === 'pending' ? (
              <TabsTrigger key={t} value={t}>
                {TAB_LABELS[t]}
                {(pendingQuery.data?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {pendingQuery.data?.length}
                  </Badge>
                )}
              </TabsTrigger>
            ) : (
              <TabsTrigger key={t} value={t}>
                {TAB_LABELS[t]}
              </TabsTrigger>
            ),
          )}
        </TabsList>

        {(availableTabs.filter((t) => t !== 'pending') as Exclude<TabKey, 'pending'>[]).map((t) => (
          <TabsContent key={t} value={t}>
            <div className="rounded-md border mt-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Unit / Gate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersQuery.isError ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <div
                          role="alert"
                          className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 my-2"
                        >
                          <p className="text-sm font-medium">Couldn&apos;t load users</p>
                          <p className="text-sm text-muted-foreground">Please try again later.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : usersQuery.isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-[var(--text-tertiary)] py-10"
                      >
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u: PmUser) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <p className="font-medium">{u.fullName}</p>
                          <p className="text-xs text-[var(--text-tertiary)]">{u.email}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {ROLE_LABELS[u.role] ?? u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{u.unit ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANTS[u.status]} className="text-xs">
                            {u.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-[var(--text-tertiary)]">
                          {u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {u.role === 'resident' && !u.unit && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setAssignUnitState({
                                    userId: u.id,
                                    userName: u.fullName,
                                  })
                                }
                              >
                                Assign Unit
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setSuspendState({
                                  userId: u.id,
                                  action: u.status === 'suspended' ? 'reinstate' : 'suspend',
                                })
                              }
                            >
                              {u.status === 'suspended' ? 'Reinstate' : 'Suspend'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <PaginationControls
              currentPage={page}
              totalPages={usersQuery.data?.meta?.totalPages ?? 1}
              onPageChange={setPage}
              disabled={usersQuery.isPlaceholderData}
            />
          </TabsContent>
        ))}

        <TabsContent value="pending">
          <div className="rounded-md border mt-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingQuery.isError ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <div
                        role="alert"
                        className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 my-2"
                      >
                        <p className="text-sm font-medium">
                          Couldn&apos;t load pending registrations
                        </p>
                        <p className="text-sm text-muted-foreground">Please try again later.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : pendingQuery.isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 4 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : pending.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-[var(--text-tertiary)] py-10"
                    >
                      No pending registrations
                    </TableCell>
                  </TableRow>
                ) : (
                  pending.map((p: PendingRegistration) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <p className="font-medium">{p.fullName}</p>
                        <p className="text-xs text-[var(--text-tertiary)]">{p.email}</p>
                        {p.phone && (
                          <p className="text-xs text-[var(--text-tertiary)]">{p.phone}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.unit ? (
                          <div>
                            <span>{p.unit}</span>
                            {!p.unitConfirmed && (
                              <p className="text-xs text-[var(--text-tertiary)]">
                                Typed by resident — confirm or correct
                              </p>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-[var(--text-tertiary)]">
                        {new Date(p.submittedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setAssignUnitState({ userId: p.id, userName: p.fullName })
                            }
                          >
                            {p.unitConfirmed ? 'Change Unit' : 'Assign Unit'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => approveMutation.mutate(p.id)}
                            disabled={approveMutation.isPending}
                          >
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setRejectTarget(p.id)}>
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!suspendState}
        title={suspendState?.action === 'suspend' ? 'Suspend User' : 'Reinstate User'}
        description={
          suspendState?.action === 'suspend'
            ? 'This will invalidate all active sessions and cancel pending invitations.'
            : 'This will reactivate the user account.'
        }
        confirmLabel={suspendState?.action === 'suspend' ? 'Suspend' : 'Reinstate'}
        loading={suspendMutation.isPending}
        onConfirm={() => suspendMutation.mutate()}
        onCancel={() => setSuspendState(null)}
      />

      <ConfirmDialog
        open={!!rejectTarget}
        title="Reject Registration"
        description="Provide a reason. A rejection email will be sent to the applicant."
        confirmLabel="Reject"
        requireReason
        reasonLabel="Rejection reason"
        minReasonLength={10}
        loading={rejectMutation.isPending}
        onConfirm={(reason) =>
          rejectMutation.mutate({ userId: rejectTarget ?? '', reason: reason ?? '' })
        }
        onCancel={() => setRejectTarget(null)}
      />

      {inviteRole && (
        <InviteUserDialog
          open={!!inviteRole}
          onOpenChange={(next) => {
            if (!next) setInviteRole(null);
          }}
          propertyId={propertyId}
          role={inviteRole}
        />
      )}

      <Dialog
        open={!!assignUnitState}
        onOpenChange={(open) => {
          if (!open) {
            setAssignUnitState(null);
            setSelectedUnitId('');
          }
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Assign Unit</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Select the unit for <span className="font-medium">{assignUnitState?.userName}</span>.
          </p>
          <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a unit" />
            </SelectTrigger>
            <SelectContent>
              {(unitsQuery.data?.data ?? []).map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.unitNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAssignUnitState(null);
                setSelectedUnitId('');
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedUnitId || assignUnitMutation.isPending}
              onClick={() => {
                if (assignUnitState && selectedUnitId) {
                  assignUnitMutation.mutate({
                    userId: assignUnitState.userId,
                    unitId: selectedUnitId,
                  });
                }
              }}
            >
              {assignUnitMutation.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
